/**
 * Bounded audio ring buffer (INV-6) for 16kHz mono audio.
 * Overwrites oldest data on overflow to guarantee bounded latency.
 */
export class AudioRingBuffer {
  private readonly buffer: Float32Array;
  private readonly capacity: number;
  private writeIndex = 0;
  private totalWritten = 0;

  constructor(maxDurationSeconds = 20, sampleRate = 16000) {
    this.capacity = maxDurationSeconds * sampleRate;
    this.buffer = new Float32Array(this.capacity);
  }

  get sampleCapacity(): number {
    return this.capacity;
  }

  get totalSamplesWritten(): number {
    return this.totalWritten;
  }

  get fillFraction(): number {
    return Math.min(1.0, this.totalWritten / this.capacity);
  }

  /**
   * Writes incoming 16kHz mono chunk into the ring buffer
   */
  write(chunk: Float32Array): void {
    const len = chunk.length;
    if (len >= this.capacity) {
      // Chunk exceeds total capacity, keep only tail
      this.buffer.set(chunk.subarray(len - this.capacity));
      this.writeIndex = 0;
      this.totalWritten += len;
      return;
    }

    const firstCopy = Math.min(len, this.capacity - this.writeIndex);
    this.buffer.set(chunk.subarray(0, firstCopy), this.writeIndex);

    if (firstCopy < len) {
      const secondCopy = len - firstCopy;
      this.buffer.set(chunk.subarray(firstCopy, len), 0);
      this.writeIndex = secondCopy;
    } else {
      this.writeIndex = (this.writeIndex + len) % this.capacity;
    }

    this.totalWritten += len;
  }

  /**
   * Extracts the most recent window of audio as a contiguous Float32Array
   */
  extractWindow(durationSeconds: number, sampleRate = 16000): Float32Array {
    const requestedSamples = Math.min(this.capacity, Math.round(durationSeconds * sampleRate));
    const availableSamples = Math.min(this.totalWritten, this.capacity);
    const count = Math.min(requestedSamples, availableSamples);

    if (count <= 0) {
      return new Float32Array(0);
    }

    const output = new Float32Array(count);
    const startIdx = (this.writeIndex - count + this.capacity) % this.capacity;

    const firstCopy = Math.min(count, this.capacity - startIdx);
    output.set(this.buffer.subarray(startIdx, startIdx + firstCopy), 0);

    if (firstCopy < count) {
      const secondCopy = count - firstCopy;
      output.set(this.buffer.subarray(0, secondCopy), firstCopy);
    }

    return output;
  }

  clear(): void {
    this.writeIndex = 0;
    this.totalWritten = 0;
    this.buffer.fill(0);
  }
}
